/**
 * Preferences Hooks
 * 
 * TanStack Query hooks for user preferences operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { preferenceService } from '../services/preference-service';
import type {
  UpdatePreferencesInput,
} from '../types/preference.types';

/**
 * Query keys for preferences queries
 */
export const preferenceKeys = {
  all: ['preferences'] as const,
  current: () => [...preferenceKeys.all, 'current'] as const,
};

/**
 * Hook to get current user's preferences
 * 
 * Performance optimizations:
 * - staleTime: 10 minutes (preferences don't change frequently)
 * - keepPreviousData: true (prevents loading state flicker on refetch)
 * - gcTime: 30 minutes (keep in cache longer)
 */
export function usePreferences() {
  return useQuery({
    queryKey: preferenceKeys.current(),
    queryFn: async () => {
      const result = await preferenceService.getPreferences();
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - preferences don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    placeholderData: (previousData) => previousData, // Prevent loading state flicker
  });
}

/**
 * Hook to update user preferences
 * 
 * Performance optimizations:
 * - Updates cache directly instead of invalidating (faster, no refetch needed)
 * - Optimistic updates for better perceived performance
 */
export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdatePreferencesInput) => {
      const result = await preferenceService.updatePreferences(input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    onSuccess: (data) => {
      // Update cache directly instead of invalidating (faster, no refetch needed)
      // This avoids unnecessary network request since we already have the updated data
      queryClient.setQueryData(preferenceKeys.current(), data);
      
      // Optionally invalidate related queries that depend on preferences
      // (e.g., dashboard queries if dashboard preferences changed)
      queryClient.invalidateQueries({ 
        queryKey: ['dashboard'],
        exact: false 
      });
    },
  });
}

